import { Component, OnInit } from '@angular/core';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { finalize } from 'rxjs/operators';
import { defer, forkJoin, } from 'rxjs';
import { DocumentItem, DocumentUpdateRequest, FileUploadRequest, FolderCreateRequest } from '../../models/document.model';
import { DocumentsService } from "@app/modules/documents/services/documents.service";
import { SecurityRoleService } from '@app/modules/security-roles/services/security-role.service';
import { SecurityFeature } from '@app/modules/security-setup/models/security-role.model';
import { features } from '@app/consts/features';
import { FormGeneratorDialogComponent } from '@app/shared/components/form-generator-dialog/form-generator-dialog.component';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { BreadCrumbItem } from "@progress/kendo-angular-navigation";
import { GridState } from '@app/shared/services/grid-state/grid-state.model';
import { RowActionButtonInput } from '@app/modules/talent-track/talent-track-edit-employee/edit-employee/components/employment-records/components/employment-record-details/components/data-grid-comp/models/data-grid-comp.model';
@Component({
    selector: 'app-documents-manager',
    templateUrl: './documents-manager.component.html',
    styleUrls: ['./documents-manager.component.scss']
})
export class DocumentsManagerComponent implements OnInit {
    public gridView: GridDataResult;
    public pageSize = 10;
    public skip = 0;
    public currentFolderId = '';
    public documents: DocumentItem[] = [];
    public totalItems = 0;
    public currentPage = 0;

    // Loading states
    public isLoading: boolean = true;
    public isCreatingFolder: boolean = false;
    public isUploadingFile: boolean = false;
    public isDeletingItem: boolean = false;
    public isUpdatingItem: boolean = false;

    // Current user features
    public currentUserFeatures: SecurityFeature[] = [];
    public hasDocumentsLibraryAdministration: boolean = false;
    public hasDocumentsAccess: boolean = false;
    public hasDocumentsPublisher: boolean = false;

    uploadFileFormId: string = 'frm_XTw1OArYyAizaA';
    editFileFormId: string = 'frm_fmdyEucM3L0MbA';
    createFolderFormId: string = 'frm_Q6sOLyGeCkq53Z';
    breadcrumbs: BreadCrumbItem[] = [
        {
            text: "Home",
            title: "Home"
        }
    ];
    dialogRef: MatDialogRef<any>;

    gridId = 'documents-manager-grid';
    public columns: any[] = [
        { field: 'contentType', title: '', type: 'fileType' },
        { field: 'name', title: this.translate.instant('DocumentsLibrary-Name'), localizationCode: 'DocumentsLibrary-Name' },
        { field: 'status', subField: "name", title: this.translate.instant('DocumentsLibrary-Status'), localizationCode: 'DocumentsLibrary-Status', type: 'chip', chipValue: ['Published', 'Archived', 'Draft'] },
        { field: 'fileSize', title: this.translate.instant('DocumentsLibrary-FileSize'), localizationCode: 'DocumentsLibrary-FileSize', type: 'number' },
        { field: 'version', subField: "createdBy", title: this.translate.instant('DocumentsLibrary-CreatedBy'), localizationCode: 'DocumentsLibrary-CreatedBy', type: 'employee' },
        { field: 'version', subField: 'createdOn', title: this.translate.instant('DocumentsLibrary-CreatedOn'), localizationCode: 'DocumentsLibrary-CreatedOn', type: 'dateTime' },
    ];
    filterCategories: any[] = [
        { field: 'status.name', title: this.translate.instant('DocumentsLibrary-Status'), type: 'String', dataType: 'String' },
    ];
    sortableColumns: any[] = [
        { field: "name", sortValue: "name" },
        { field: "status", sortValue: "status.name" },
        { field: "fileSize", sortValue: "fileSize" },
    ];
    rowSelectionSettings: any = {
        canSelectRows: true,
        selectableMode: 'multiple',
        selectRowBy: 'id'
    }
    gridActionButtons: RowActionButtonInput[] = [];
    gridDataResult: { data: any[]; total: number; };
    sortString: string;
    filterString: string;
    getRequest: any;
    searchFilterString: string;
    searchValue: string;
    clearSelectedItems: any;

    constructor(
        public translate: TranslateService,
        private dialog: MatDialog,
        private snackbarService: SnackbarService,
        private documentsService: DocumentsService,
        private securityRoleService: SecurityRoleService,
        private overlayService: OverlayService,
    ) {
    }

    ngOnInit(): void {
        this.loadCurrentUserFeatures();
    }

    loadDocuments(): void {
        this.isLoading = true;
        let filter: string = '';

        if (this.currentFolderId && this.currentFolderId.trim() !== '') {
            filter = `(inFolder.id = "${this.currentFolderId}")`;
        }
        else if (this.currentFolderId === '') {
            filter = `(inFolder.id = null)`;
        }

        if (this.filterString && this.filterString.trim() !== '') {
            filter = `(${filter}) AND (${this.filterString})`;
        }

        if (this.searchFilterString && this.searchFilterString.trim() !== '') {
            filter = `(${filter}) AND (${this.searchFilterString})`;
        }

        this.getRequest = this.documentsService.getFolderContents(
            this.pageSize,
            this.skip,
            filter,
            this.sortString
        )
        .subscribe(
            {
            next: (v) => {
                v.data.forEach(item => {
                    if (item.isFolder) {
                        item.contentType = 'folder';
                    }
                });

                this.gridDataResult = {
                    data: v.data,
                    total: v.total,
                }
                this.isLoading = false;
            },
            error: (e) => {this.overlayService.hide(); this.isLoading = false;},
                complete: () => {this.overlayService.hide(); this.isLoading = false;}
            }
        );
    }

    navigateToFolder(folder: DocumentItem): void {
        if (folder.isFolder) {
            this.breadcrumbs = [
                ...this.breadcrumbs,
                {
                    text: folder.name,
                    title: folder.id,
                }
            ];
            this.currentFolderId = folder.id;
            this.currentPage = 0;
            this.skip = 0;
            this.loadDocuments();
        }
    }

    onBreadcrumbClick(item: BreadCrumbItem): void {
        const index = this.breadcrumbs.findIndex((e) => e.text === item.text);
        this.breadcrumbs = this.breadcrumbs.slice(0, index + 1);
        if (item.title === "Home") {
            this.currentFolderId = '';
        }
        else {
            this.currentFolderId = item.title as string;
        }
        this.currentPage = 0;
        this.skip = 0;
        this.loadDocuments();
    }

    uploadFile(fileUploadRequest: FileUploadRequest): void {
        this.isUploadingFile = true;

        this.documentsService.createFile(fileUploadRequest)
            .pipe(
                finalize(() => {
                    this.isUploadingFile = false;
                })
            )
            .subscribe({
                next: () => {
                    this.dialogRef.close();
                    this.loadDocuments();
                    this.snackbarService.openSnackBar(`${this.translate.instant('SavedSuccessfully')}`, 'clear', 'success');
                },
                error: (error) => {
                    console.error('Error uploading file:', error);
                }
            });
    }

    createFolder(createRequest: FolderCreateRequest): void {
        this.overlayService.show();

        this.documentsService.createFolder(createRequest)
            .pipe(
                finalize(() => {
                    this.overlayService.hide();
                })
            )
            .subscribe({
                next: () => {
                    this.dialogRef.close();
                    this.loadDocuments();
                    this.snackbarService.openSnackBar(`${this.translate.instant('FolderCreatedSuccessfully')}`, 'clear', 'success');
                },
                error: (error) => {
                    console.error('Error creating folder:', error);
                }
            });
    }

    openDialog(document: DocumentItem): void {
        if (document.isFolder) {
            this.openFolderDialog(document);
        }
        else {
            this.openFileDialog(document);
        }
    }

    downloadFile(document: DocumentItem): void {
        if (!document.isFolder) {
            this.documentsService.downloadFile(document.id)
                .subscribe({
                    next: (blob: Blob) => {
                        const downloadLink = window.document.createElement('a');
                        const url = window.URL.createObjectURL(blob);
                        downloadLink.href = url;
                        downloadLink.download = document.name;
                        downloadLink.click();
                        window.URL.revokeObjectURL(url);
                    },
                    error: (error) => {
                        console.error('Error downloading file:', error);
                    }
                });
        }
    }

    deleteItem(item: DocumentItem): void {
        this.isDeletingItem = true;

        this.documentsService.deleteItem(item.id)
            .pipe(
                finalize(() => {
                    this.isDeletingItem = false;
                })
            )
            .subscribe({
                next: () => {
                    this.loadDocuments();
                    this.snackbarService.openSnackBar(`${this.translate.instant('DeletedSuccessfully')}`, 'clear', 'success');
                },
                error: (error) => {
                    console.error('Error deleting item:', error);
                }
            });
    }

    

    openFolderDialog(folder?: DocumentItem) {
        let formData = {
            id: folder ? folder.id : null,
            name: folder ? folder.name : null,
            comments: folder ? folder.comments : null,
            status: folder && folder.status ? folder.status.id : 'Draft',
            workLocations: folder && folder.workLocations
                ? folder.workLocations.map(wl => ({ id: wl.id, name: wl.name }))
                : [],
            roles: folder && folder.roles
                ? folder.roles.map(role => ({ id: role.id, name: role.name }))
                : []
        };

        const dialogConfig = new MatDialogConfig();

        dialogConfig.disableClose = true;
        dialogConfig.autoFocus = true;

        dialogConfig.data = {
            formId: this.createFolderFormId,
            formData: formData,
        };

        this.dialogRef = this.dialog.open(FormGeneratorDialogComponent, dialogConfig);

        const sub = this.dialogRef.componentInstance.emitFormData.subscribe((event) => {
            event.workLocations = event.workLocations?.map(item => item.id);
            event.roles = event.roles?.map(item => item.id);
            event.inFolder = this.currentFolderId;

            if (folder) {
                this.updateFolder(event);
            }
            else {
                this.createFolder(event);
            }
        });
    }

    getFileDetails(file: DocumentItem) {
        this.overlayService.show('Loading');

        this.documentsService.getFile(file.id).subscribe({
            next: (fileDetails: DocumentItem) => {
                this.overlayService.hide();
                if (fileDetails.isFolder) {
                    this.openFolderDialog(fileDetails);
                }
                else {
                    this.openFileDialog(fileDetails);
                }
            },
            error: (error) => {
                console.error('Error fetching file details:', error);
                this.overlayService.hide();
            }
        });
    }

    openFileDialog(file?: DocumentItem) {
        let formData = {
            id: file ? file.id : null,
            file: null,
            name: file ? file.name : null,
            comments: file ? file.comments : null,
            inFolder: this.currentFolderId,
            status: file && file.status ? file.status.id : 'Draft',
            workLocations: file && file.workLocations
                ? file.workLocations.map(wl => ({ id: wl.id, name: wl.name }))
                : [],
            roles: file && file.roles
                ? file.roles.map(role => ({ id: role.id, name: role.name }))
                : []
        };

        const dialogConfig = new MatDialogConfig();

        dialogConfig.disableClose = true;
        dialogConfig.autoFocus = true;

        dialogConfig.data = {
            formId: file ? this.editFileFormId : this.uploadFileFormId,
            formData: formData,
        };

        this.dialogRef = this.dialog.open(FormGeneratorDialogComponent, dialogConfig);

        const sub = this.dialogRef.componentInstance.emitFormData.subscribe((event) => {
            event.workLocations = event.workLocations?.map(item => item.id);
            event.roles = event.roles?.map(item => item.id);
            event.inFolder = this.currentFolderId;

            if (file) {
                this.updateFile(event);
            }
            else {
                event.file = event.file[0];
                this.uploadFile(event);
            }
        });
    }

    updateFile(updateData): void {
        this.overlayService.show();

        const updateRequest = {
            id: updateData.id,
            name: updateData.name,
            comments: updateData.comments,
            workLocations: updateData.workLocations,
            roles: updateData.roles,
            status: updateData.status
        };

        this.documentsService.updateFile(updateData.id, updateRequest)
            .pipe(
                finalize(() => {
                    this.overlayService.hide();
                })
            )
            .subscribe({
                next: () => {
                    this.dialogRef.close();
                    this.loadDocuments();
                    this.snackbarService.openSnackBar(`${this.translate.instant('UpdatedSuccessfully')}`, 'clear', 'success');
                },
                error: (error) => {
                    console.error('Error updating file:', error);
                }
            });
    }

    updateFolder(updateData): void {
        this.overlayService.show();

        const updateRequest: DocumentUpdateRequest = {
            id: updateData.id,
            name: updateData.name,
            comments: updateData.comments,
            status: updateData.status,
            workLocations: updateData.workLocations,
            roles: updateData.roles
        };

        this.documentsService.updateFolder(updateData.id, updateRequest)
            .pipe(
                finalize(() => {
                    this.overlayService.hide();
                })
            )
            .subscribe({
                next: () => {
                    this.dialogRef.close();
                    this.loadDocuments();
                    this.snackbarService.openSnackBar(`${this.translate.instant('UpdatedSuccessfully')}`, 'clear', 'success');
                },
                error: (error) => {
                    console.error('Error updating folder:', error);
                }
            });
    }

    private loadCurrentUserFeatures(): void {
        // Fetch the current user's features directly
        this.securityRoleService.getCurrentUserFeatures()
            .subscribe({
                next: (fs: SecurityFeature[]) => {
                    this.currentUserFeatures = fs;

                    this.hasDocumentsLibraryAdministration = this.currentUserFeatures.some(f => f.id === features.DOCUMENTS_LIBRARY_ADMINISTRATION);
                    this.hasDocumentsAccess = this.currentUserFeatures.some(f => f.id === features.DOCUMENTS_ACCESS);
                    this.hasDocumentsPublisher = this.currentUserFeatures.some(f => f.id === features.DOCUMENTS_PUBLISHER);

                    if(this.hasDocumentsLibraryAdministration){
                        this.gridActionButtons = [
                            {
                            id: 'download',
                            name: this.translate.instant('Download'),
                            icon: 'download'
                            },
                            {
                            id: 'edit',
                            name: this.translate.instant('Edit'),
                            icon: 'edit'
                            }
                        ];
                    } else if(this.hasDocumentsPublisher){
                        this.gridActionButtons = [
                            {
                            id: 'download',
                            name: this.translate.instant('Download'),
                            icon: 'download'
                            },
                            {
                            id: 'edit',
                            name: this.translate.instant('Edit'),
                            icon: 'edit'
                            }
                        ];
                    } else if(this.hasDocumentsAccess){
                        this.gridActionButtons = [
                            {
                            id: 'download',
                            name: this.translate.instant('Download'),
                            icon: 'download'
                            }
                        ];
                    } else {
                        this.gridActionButtons = [];
                    }

                    this.loadDocuments();
                },
                error: (error) => {
                    console.error('Error fetching current user features:', error);
                }
            });
    }

    public canModifyDocuments(): boolean {
        // Users can modify documents if they have either:
        // - DocumentsLibraryAdministration (full admin rights)
        // - DocumentsPublisher (can publish/modify documents)
        // But NOT if they only have DocumentsAccess (read-only)
        return this.hasDocumentsLibraryAdministration || this.hasDocumentsPublisher;
    }

    public hasAnyActions(dataItem: DocumentItem): boolean {
        const canDownload = !dataItem.isFolder;
        const canModify = this.canModifyDocuments();
        return canDownload || canModify;
    }

    deleteAllSelected(itemsToDelete: string[]) {
        const observables = itemsToDelete.map(item => defer(() => this.documentsService.deleteItem(item)));

        this.overlayService.show();

        forkJoin(observables)
            .pipe(
                finalize(() => this.overlayService.hide())
            )
            .subscribe(
                (res) => {
                    this.clearSelectedItems = !this.clearSelectedItems;
                    this.loadDocuments();
                    this.snackbarService.openSnackBar('Deleted successfully', 'clear', 'success');
                },
                err => {
                    this.snackbarService.openSnackBar(err, 'clear', 'warn');
                }
            );
    }

    actionButtonClicked(event: {id: string, rowData: DocumentItem}) {
        if(event.id === 'download') {
            if(event?.rowData.isFolder === true) {
                this.snackbarService.openSnackBar(this.translate.instant('FoldersCannotBeDownloaded'), 'clear', 'warn');
            }
            else {
                this.downloadFile(event.rowData);
            }
        }
        else if(event.id === 'edit') {
            this.getFileDetails(event.rowData);
        }
    }

    gridStateChange(gridState: GridState) {
        if (this.pageSize !== gridState.pageSize) {
          this.pageSize = gridState.pageSize;
        }
        if (this.skip !== gridState.skip) {
          this.skip = gridState.skip;
        }
        if (this.sortString !== gridState.sortString) {
          this.sortString = gridState.sortString;
        }
        if (this.filterString !== gridState.filter) {
          this.filterString = gridState.filter;
          this.skip = 0;
        }
        if (this.searchValue !== gridState.searchValue) {
            this.searchFilterString = '';
            this.searchValue = gridState.searchValue;
            if(this.searchValue && this.searchValue.trim() !== ''){
                this.searchFilterString = `(Name like "${this.searchValue}")`;
            }
            this.skip = 0;
        }
    
        this.getRequest.unsubscribe();
        this.loadDocuments();
    }
}
